import { FetchSource, PMTiles, Protocol } from 'pmtiles'
import maplibregl from 'maplibre-gl'
import { useQuery } from '@tanstack/react-query'
import { useEffect, useMemo } from 'react'
import { PMTileURLs } from '~/constants'
import type { PMTileSource } from '~/types/dataModel'

export type PMTilesLayer = {
  layer: string
  attributes: PMTilesAttribute[]
  attributeCount: number
  pmTiles: PMTiles
}

export type ColorBreak = [number, string]

export type PMTilesAttribute = {
  attribute: string
  count: number
  values: string[]
  colorBreaks?: ColorBreak[]
} & ({ type: 'number'; max: number; min: number } | { type: string; max?: never; min?: never })

export type PMTilesMetadata = {
  name: string
  tilestats: {
    layers: PMTilesLayer[]
  }
}

let protocolSingleton: Protocol | null = null

function ensurePMTilesProtocolInstalled() {
  if (!protocolSingleton) {
    const protocol = new Protocol()
    maplibregl.addProtocol('pmtiles', protocol.tile)
    protocolSingleton = protocol
  }
  return protocolSingleton!
}

const fetchPMTilesData = async () => {
  const data = await Promise.all(
    Object.values(PMTileURLs).map(async (source) => {
      try {
        const p = new PMTiles(source)

        const [header, metadata] = await Promise.all([
          p.getHeader().catch((e: Error) => {
            console.error(`Header fetch failed for ${source}:`, e)
            throw e
          }),
          p.getMetadata().catch((e: Error) => {
            console.error(`Metadata fetch failed for ${source}:`, e)
            throw e
          }) as Promise<PMTilesMetadata>,
        ])

        return [header, metadata] as const
      } catch (error) {
        console.error(`Failed to load PMTiles for ${source}:`, error)
        throw error
      }
    })
  )

  return {
    headers: data.map(([header]) => header),
    metadata: data.map(([, metadata]) => metadata as PMTilesMetadata),
  }
}

function usePMTiles() {
  const sources: Record<PMTileSource, string> = useMemo(() => {
    return Object.fromEntries(
      Object.entries(PMTileURLs).map(([key, value]) => [key, value])
    ) as Record<PMTileSource, string>
  }, [])

  const pmtilesBySource: Record<PMTileSource, PMTiles> = useMemo(() => {
    const protocol = ensurePMTilesProtocolInstalled()

    const entries = Object.entries(sources).map(([sourceType, url]) => {
      const pm = new PMTiles(new FetchSource(url))
      protocol.add(pm)
      return [sourceType, pm] as const
    })

    return Object.fromEntries(entries) as Record<PMTileSource, PMTiles>
  }, [sources])

  const { data, isLoading } = useQuery({
    queryKey: ['pmtiles'],
    queryFn: fetchPMTilesData,
    staleTime: Infinity,
  })

  useEffect(() => {
    ensurePMTilesProtocolInstalled()
  }, [])

  const layerInfo = useMemo(() => {
    if (!data?.metadata) return undefined

    return Object.keys(sources).reduce(
      (acc, sourceType) => {
        const index = Object.keys(sources).indexOf(sourceType)

        acc[sourceType as PMTileSource] = data.metadata[index]!.tilestats?.layers.map((l) => ({
          ...l,
          pmTiles: pmtilesBySource[sourceType as PMTileSource],
        }))
        return acc
      },
      {} as Record<PMTileSource, PMTilesLayer[]>
    )
  }, [data?.metadata, sources, pmtilesBySource])

  return {
    headers: data?.headers,
    metadata: data?.metadata,
    layerInfo,
    isLoading,
    sources,
  }
}

export default usePMTiles
